import { useEffect, useState } from "react";
import { PageShell, PageHero } from "@project/user/components/page-shell";
import { ManavSevaBankDetails } from "@project/user/components/manav-seva-bank-details";
import { Button } from "@project/components/ui/button";
import { Badge } from "@project/components/ui/badge";
import { useI18n } from "@project/lib/use-i18n";
import Marquee from "react-fast-marquee";
import {
  UtensilsCrossed,
  HeartHandshake,
  Users,
  Heart,
  Check,
  Clock,
  CalendarCheck,
  Leaf,
  Quote,
  BadgeCheck,
} from "lucide-react";
import mealImg from "@project/assets/home-meal.webp";
import {
  TiffinSevaInquiryModal,
  type AgeCategory,
} from "@project/user/components/tiffin-seva-inquiry-modal";
import { TiffinSevaDonationModal } from "@project/user/components/tiffin-seva-donation-modal";
import { publicTiffinSevaDonationService } from "@project/services/donate/tiffin-seva-donation.service";
import {
  Reveal,
  RevealText,
  Stagger,
  StaggerItem,
} from "@project/user/components/motion";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@project/components/ui/dialog";

// 4 Specialization Highlights
const SPECIALIZATIONS = [
  {
    step: "01",
    titleKey: "programs.tiffinSeva.specialization.items.jainFood.title",
    taglineKey: "programs.tiffinSeva.specialization.items.jainFood.tagline",
    descKey: "programs.tiffinSeva.specialization.items.jainFood.desc",
    icon: Leaf,
    badgeKey: "programs.tiffinSeva.specialization.items.jainFood.badge",
    accentBg: "from-emerald-500/15 to-emerald-500/5",
    iconBg: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
    borderHover: "hover:border-emerald-500/50",
  },
  {
    step: "02",
    titleKey: "programs.tiffinSeva.specialization.items.delivery.title",
    taglineKey: "programs.tiffinSeva.specialization.items.delivery.tagline",
    descKey: "programs.tiffinSeva.specialization.items.delivery.desc",
    icon: Clock,
    badgeKey: "programs.tiffinSeva.specialization.items.delivery.badge",
    accentBg: "from-amber-500/15 to-amber-500/5",
    iconBg: "bg-amber-500/10 text-amber-700 dark:text-amber-400",
    borderHover: "hover:border-amber-500/50",
  },
  {
    step: "03",
    titleKey: "programs.tiffinSeva.specialization.items.regular.title",
    taglineKey: "programs.tiffinSeva.specialization.items.regular.tagline",
    descKey: "programs.tiffinSeva.specialization.items.regular.desc",
    icon: CalendarCheck,
    badgeKey: "programs.tiffinSeva.specialization.items.regular.badge",
    accentBg: "from-blue-500/15 to-blue-500/5",
    iconBg: "bg-blue-500/10 text-blue-700 dark:text-blue-400",
    borderHover: "hover:border-blue-500/50",
  },
  {
    step: "04",
    titleKey: "programs.tiffinSeva.specialization.items.authentic.title",
    taglineKey: "programs.tiffinSeva.specialization.items.authentic.tagline",
    descKey: "programs.tiffinSeva.specialization.items.authentic.desc",
    icon: UtensilsCrossed,
    badgeKey: "programs.tiffinSeva.specialization.items.authentic.badge",
    accentBg: "from-rose-500/15 to-rose-500/5",
    iconBg: "bg-rose-500/10 text-rose-700 dark:text-rose-400",
    borderHover: "hover:border-rose-500/50",
  },
];

// Eligibility Cards
const ELIGIBILITY_CARDS = [
  {
    titleKey: "programs.tiffinSeva.eligibility.cards.senior.title",
    descKey: "programs.tiffinSeva.eligibility.cards.senior.desc",
    icon: Users,
    tagKey: "programs.tiffinSeva.eligibility.cards.senior.tag",
    category: "above_60" as AgeCategory,
  },
  {
    titleKey: "programs.tiffinSeva.eligibility.cards.general.title",
    descKey: "programs.tiffinSeva.eligibility.cards.general.desc",
    icon: HeartHandshake,
    tagKey: "programs.tiffinSeva.eligibility.cards.general.tag",
    category: "below_60" as AgeCategory,
  },
  {
    titleKey: "programs.tiffinSeva.eligibility.cards.divyang.title",
    descKey: "programs.tiffinSeva.eligibility.cards.divyang.desc",
    icon: Heart,
    tagKey: "programs.tiffinSeva.eligibility.cards.divyang.tag",
    category: "below_60" as AgeCategory,
  },
];

// How It Works Steps
/* const STEPS = [
  { step: "01", titleKey: "programs.tiffinSeva.howItWorks.steps.step1.title", descKey: "programs.tiffinSeva.howItWorks.steps.step1.desc" },
  { step: "02", titleKey: "programs.tiffinSeva.howItWorks.steps.step2.title", descKey: "programs.tiffinSeva.howItWorks.steps.step2.desc" },
  { step: "03", titleKey: "programs.tiffinSeva.howItWorks.steps.step3.title", descKey: "programs.tiffinSeva.howItWorks.steps.step3.desc" },
  { step: "04", titleKey: "programs.tiffinSeva.howItWorks.steps.step4.title", descKey: "programs.tiffinSeva.howItWorks.steps.step4.desc" },
]; */

export default function TiffinPage() {
  const { t } = useI18n();
  // Modal states
  const [inquiryModalOpen, setInquiryModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<AgeCategory | undefined>(undefined);
  const [donationModalOpen, setDonationModalOpen] = useState(false);

  // Schemes states
  const [schemes, setSchemes] = useState<any[]>([]);
  const [loadingSchemes, setLoadingSchemes] = useState(true);
  const [selectedSchemeId, setSelectedSchemeId] = useState<number | undefined>(undefined);

  useEffect(() => {
    setLoadingSchemes(true);
    publicTiffinSevaDonationService.listActiveSchemes()
      .then(res => {
        setSchemes(res);
      })
      .catch(err => {
        console.error("Failed to load tiffin schemes:", err);
      })
      .finally(() => {
        setLoadingSchemes(false);
      });
  }, []);

  const handleOpenDonation = (schemeId: number) => {
    setSelectedSchemeId(schemeId);
    setDonationModalOpen(true);
  };

  const handleOpenModal = (cat?: AgeCategory) => {
    setSelectedCategory(cat);
    setInquiryModalOpen(true);
  };

  return (
    <PageShell>
      {/* Hero Banner */}
      <PageHero
        eyebrow={t("programs.tiffinSeva.hero.eyebrow")}
        title={t("programs.tiffinSeva.hero.title")}
        subtitle={t("programs.tiffinSeva.hero.subtitle")}
        image={mealImg}
      />

      {/* Main Content Area */}
      <section className="py-10 px-4 max-w-site mx-auto space-y-14">
        {/* About Tiffin Seva & Poem Pull-quote grid */}
        <div className="grid gap-8 md:grid-cols-12 items-start">
          {/* Content (left) */}
          <div className="md:col-span-7 flex flex-col justify-start py-4 pr-0 md:pr-6 gap-5">
            <Reveal variant="left" className="space-y-4">
              <span className="text-xs font-bold text-primary tracking-widest uppercase">
                {t("programs.tiffinSeva.about.eyebrow")}
              </span>
              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-foreground tracking-tight">
                <RevealText delay={120}>
                  {t("programs.tiffinSeva.about.title")}
                </RevealText>
              </h2>
              <p className="font-serif italic !text-[1.1rem] leading-relaxed text-primary">
                {t("programs.tiffinSeva.about.tagline")}
              </p>
              <div className="text-xs sm:text-sm text-muted-foreground leading-relaxed space-y-3.5">
                {t("programs.tiffinSeva.about.body")
                  .split("\n\n")
                  .map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))}
              </div>
            </Reveal>

            {/* Action Buttons: Apply for Tiffin Seva & Support and Donate */}
            <Reveal
              variant="fade"
              delay={300}
              className="flex flex-wrap items-center gap-3 pt-6"
            >
              <Button
                size="sm"
                onClick={() => handleOpenModal()}
                className="btn-lift bg-primary p-5 text-primary-foreground font-semibold text-sm gap-1.5 shadow-sm"
              >
                <UtensilsCrossed className="h-3.5 w-3.5" /> {t("programs.tiffinSeva.about.applyBtn")}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setDonationModalOpen(true)}
                className="btn-lift border-primary/40 p-5 text-primary hover:bg-primary/10 font-semibold text-sm gap-1.5 shadow-xs transition-all"
              >
                <HeartHandshake className="h-3.5 w-3.5 text-rose-500" /> {t("programs.tiffinSeva.about.donateBtn")}
              </Button>
            </Reveal>
          </div>

          {/* Poem Pull-quote Card (right) */}
          <Reveal variant="morph" delay={250} className="md:col-span-5 flex">
            <figure className="shadow-foreground/5 relative flex w-full flex-col justify-between rounded-3xl border border-border bg-card p-7 shadow-xl sm:p-9">
              <div className="absolute -top-5 left-7 grid h-10 w-10 place-items-center rounded-full bg-gold text-gold-foreground shadow">
                <Quote className="h-5 w-5" />
              </div>

              <div className="mt-4 flex-1">
                {/* Intro Line */}
                <p className="mb-4 whitespace-pre-line !text-[1.2rem] italic leading-snug text-primary">
                  {t("programs.tiffinSeva.poem.intro")}
                </p>

                {/* 3 Line Preview of Poem */}
                <div className="border-gold/40 space-y-3 border-l-2 py-1 pl-4">
                  {(() => {
                    const rawLines = t("programs.tiffinSeva.poem.lines", {
                      returnObjects: true,
                    });
                    const lines = Array.isArray(rawLines)
                      ? (rawLines as string[])
                      : [];
                    return lines.slice(0, 3).map(line => (
                      <p
                        key={line}
                        className="!text-[1rem] italic !leading-[1.3] leading-relaxed text-foreground"
                      >
                        {line}
                      </p>
                    ));
                  })()}
                  <div className="mt-1 flex items-center gap-2 font-serif !text-[1.2rem] italic text-muted-foreground">
                    <span>...</span>
                    {/* Read More Dialog */}
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button
                          variant="link"
                          className="mt-2 flex h-auto cursor-pointer items-center gap-1 p-0 font-sans text-xs font-semibold not-italic text-primary underline transition-colors hover:text-gold"
                        >
                          {t("programs.tiffinSeva.poem.readMore")}
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="flex max-h-[85vh] max-w-2xl flex-col overflow-hidden border-border bg-card p-0">
                        <DialogHeader className="bg-muted/30 border-b border-border p-6 pb-4">
                          <DialogTitle className="flex items-center gap-2 font-serif text-2xl text-primary">
                            <Quote className="h-5 w-5 shrink-0 text-gold" />
                            {t("programs.tiffinSeva.poem.title")}
                          </DialogTitle>
                        </DialogHeader>

                        <div className="flex-1 space-y-6 overflow-y-auto p-6 md:p-8">
                          <p className="border-border/60 whitespace-pre-line border-b pb-4 !text-[1.1rem] text-lg italic !leading-[1.5] text-primary">
                            {t("programs.tiffinSeva.poem.intro")}
                          </p>

                          <div className="border-gold/50 space-y-3 border-l-2 pl-4 text-base leading-relaxed text-foreground md:text-lg">
                            {(() => {
                              const rawLines = t(
                                "programs.tiffinSeva.poem.lines",
                                {
                                  returnObjects: true,
                                }
                              );
                              const lines = Array.isArray(rawLines)
                                ? (rawLines as string[])
                                : [];
                              return lines.map(line => (
                                <p
                                  key={line}
                                  className="py-0.5 !text-[1rem] !leading-[1.3] transition-colors hover:text-primary"
                                >
                                  {line}
                                </p>
                              ));
                            })()}
                          </div>

                          <div className="bg-muted/20 mt-8 flex flex-col gap-4 rounded-xl border-t border-border p-4 pt-6 text-sm sm:flex-row sm:items-center sm:justify-between">
                            <div>
                              <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                {t("programs.tiffinSeva.poem.authorLabel")}
                              </span>
                              <span className="mt-0.5 block font-serif text-base font-bold text-foreground">
                                {t("programs.tiffinSeva.poem.authorName")}
                              </span>
                            </div>
                            <div>
                              <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                Location / Foundation
                              </span>
                              <span className="mt-0.5 block font-serif text-base text-foreground">
                                {t("programs.tiffinSeva.poem.foundation")}
                              </span>
                            </div>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
              </div>

              <figcaption className="mt-6 border-t border-border pt-4">
                <div className="text-sm font-semibold text-foreground">
                  {t("programs.tiffinSeva.poem.authorName")}
                </div>
                <div className="mt-0.5 text-xs uppercase tracking-wider text-muted-foreground font-medium">
                  {t("programs.tiffinSeva.poem.foundation")}
                </div>
              </figcaption>

              <div className="bg-primary/10 pointer-events-none absolute -bottom-2 -right-2 grid h-12 w-12 place-items-center rounded-2xl text-primary">
                <BadgeCheck className="h-6 w-6" />
              </div>
            </figure>
          </Reveal>
        </div>

        {/* TIFFIN SEVA SPECIALIZATION SECTION (4 POINTS WITH INTERACTIVE ICONS) */}
        <div className="space-y-8">
          <Reveal className="text-center max-w-2xl mx-auto space-y-1.5">
            <Badge variant="secondary" className="bg-primary/10 text-primary font-bold text-xs px-3 py-1">
              {t("programs.tiffinSeva.specialization.badge")}
            </Badge>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-foreground tracking-tight font-serif">
              {t("programs.tiffinSeva.specialization.title")}
            </h2>
            <p className="text-xs sm:text-sm text-muted-foreground">
              {t("programs.tiffinSeva.specialization.desc")}
            </p>
          </Reveal>

          <Stagger className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4" delay={150}>
            {SPECIALIZATIONS.map((item, index) => {
              const Icon = item.icon;
              return (
                <StaggerItem key={item.titleKey} index={index}>
                <div
                  className={`group relative h-full rounded-3xl border border-border bg-gradient-to-b ${item.accentBg} p-6 shadow-xs transition-all duration-300 hover:-translate-y-1.5 hover:shadow-lg ${item.borderHover} cursor-default`}
                >
                  {/* Top Bar with Icon & Badge */}
                  <div className="flex items-center justify-between">
                    <div
                      className={`flex h-13 w-13 items-center justify-center rounded-2xl ${item.iconBg} shadow-xs transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3`}
                    >
                      <Icon className="h-6 w-6" />
                    </div>
                    <Badge variant="outline" className="font-bold text-[10px] bg-background/80 backdrop-blur-xs">
                      {t(item.badgeKey)}
                    </Badge>
                  </div>

                  {/* Title & Tagline */}
                  <div className="mt-5 space-y-1">
                    <span className="text-[11px] font-bold text-primary tracking-wider uppercase">
                      {item.step}. {t(item.taglineKey)}
                    </span>
                    <h3 className="!font-sans !text-xl sm:!text-2xl !font-semibold text-foreground group-hover:text-primary transition-colors">
                      {t(item.titleKey)}
                    </h3>
                  </div>

                  {/* Description */}
                  <p className="mt-2.5 text-xs text-muted-foreground leading-relaxed">
                    {t(item.descKey)}
                  </p>
                </div>
                </StaggerItem>
              );
            })}
          </Stagger>
        </div>


        {/* Eligibility Section */}
        <div className="space-y-8 pt-4">
          <Reveal variant="morph" className="text-center max-w-2xl mx-auto">
            <Badge variant="secondary" className="bg-primary/10 text-primary font-bold text-xs px-3 py-1">
              {t("programs.tiffinSeva.eligibility.badge")}
            </Badge>
            <h2 className="mt-1.5 text-2xl sm:text-3xl font-extrabold text-foreground tracking-tight font-serif">
              {t("programs.tiffinSeva.eligibility.title")}
            </h2>
          </Reveal>

          <Stagger className="grid gap-8 sm:grid-cols-3 max-w-6xl mx-auto" step={140}>
            {ELIGIBILITY_CARDS.map((c, idx) => {
              const Icon = c.icon;
              return (
                <StaggerItem key={idx} index={idx} from="zoom">
                <div className="h-full space-y-3 rounded-2xl border border-border bg-card p-5 shadow-xs">
                  <div className="flex items-center justify-between">
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-sm">
                      <Icon className="h-6 w-6" />
                    </div>
                    <Badge variant="outline" className="text-[10px] font-bold">
                      {t(c.tagKey)}
                    </Badge>
                  </div>
                  <h3 className=" font-bold text-foreground !font-sans !text-2xl tracking-tight">{t(c.titleKey)}</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">{t(c.descKey)}</p>
                </div>
                </StaggerItem>
              );
            })}
          </Stagger>
        </div>

        {/* How It Works */}
        {/* <div className="space-y-6 pt-6">
          <div className="text-center max-w-2xl mx-auto">
            <Badge variant="secondary" className="bg-primary/10 text-primary font-bold">
              {t("programs.tiffinSeva.howItWorks.badge")}
            </Badge>
            <h2 className="mt-2 text-2xl sm:text-3xl font-extrabold text-foreground">
              {t("programs.tiffinSeva.howItWorks.title")}
            </h2>
          </div>

          <div className="flex flex-nowrap items-center justify-start lg:justify-center overflow-x-auto py-4 px-2 scrollbar-none max-w-full">
            {STEPS.map((s, idx) => (
              <div key={idx} className="flex items-center shrink-0">
                <div className="rounded-2xl border border-border bg-card px-3.5 py-3 sm:px-5 sm:py-3.5 flex items-center gap-2 sm:gap-2.5 shadow-xs transition-all hover:border-primary/50">
                  <span className="flex h-6 w-6 sm:h-7 sm:w-7 shrink-0 items-center justify-center rounded-full bg-amber-500 text-white text-xs sm:text-sm font-extrabold shadow-xs">
                    {idx + 1}
                  </span>
                  <span className="text-xs sm:text-sm font-bold text-foreground whitespace-nowrap">{t(s.titleKey)}</span>
                </div>
                {idx < STEPS.length - 1 && (
                  <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5 text-primary/60 shrink-0 mx-1.5 sm:mx-2" />
                )}
              </div>
            ))}
          </div>
        </div> */}

        {/* Delivery info marquee */}
        <Reveal variant="wipe-left" className="border-y border-primary/20 bg-primary/[0.03] dark:bg-primary/[0.08] py-2.5 overflow-hidden">
          <Marquee pauseOnHover speed={40} gradient={false}>
            <div className="flex items-center gap-3 text-xs sm:text-sm font-semibold text-primary px-4 select-none">
              <span className="text-gold">❋</span>
              <span>🚚 Home delivery, with Best and authentic 100 % jain food</span>
              <span className="mx-8 text-primary/10">|</span>
              <span className="text-gold">❋</span>
              <span>🚚 Home delivery, with Best and authentic 100 % jain food</span>
              <span className="mx-8 text-primary/10">|</span>
              <span className="text-gold">❋</span>
              <span>🚚 Home delivery, with Best and authentic 100 % jain food</span>
              <span className="mx-8 text-primary/10">|</span>
            </div>
          </Marquee>
        </Reveal>

        {/* 2 Registration Types Showcase */}
        <div className="grid gap-6 md:grid-cols-2">
          {/* Card 1: Above 60 Years */}
          <Reveal variant="left" className="rounded-3xl border-2 border-gold-light bg-gold-light dark:bg-amber-950/10 p-6 sm:p-8 space-y-4 shadow-xs relative overflow-hidden">
            <div className="flex items-center justify-between">
              <Badge className="bg-gold text-gold-foreground font-bold text-xs px-3 py-1 border-none">
                {t("programs.tiffinSeva.registration.senior.badge")}
              </Badge>
              <span className="text-xs font-bold text-amber-800">
                {t("programs.tiffinSeva.registration.senior.tag")}
              </span>
            </div>
            <div>
              <h3 className="font-serif text-2xl font-bold text-foreground">
                {t("programs.tiffinSeva.registration.senior.title")}
              </h3>
              <p className="text-xs text-muted-foreground mt-1">
                {t("programs.tiffinSeva.registration.senior.desc")}
              </p>
            </div>
            <ul className="space-y-2 text-xs text-foreground/80">
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-amber-600 shrink-0" />
                <span>{t("programs.tiffinSeva.registration.senior.bullet1")}</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-amber-600 shrink-0" />
                <span>{t("programs.tiffinSeva.registration.senior.bullet2")}</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-amber-600 shrink-0" />
                <span>{t("programs.tiffinSeva.registration.senior.bullet3")}</span>
              </li>
            </ul>
            <Button
              onClick={() => handleOpenModal("above_60")}
              className="btn-lift w-full bg-gold hover:opacity-90 text-gold-foreground font-bold text-xs gap-1.5 border-none"
            >
              <Users className="h-4 w-4" /> {t("programs.tiffinSeva.registration.senior.btn")}
            </Button>
          </Reveal>

          {/* Card 2: Below 60 Years */}
          <Reveal
            variant="right"
            delay={120}
            className="rounded-3xl border-2 !border-primary/50 bg-primary/[0.03] dark:bg-primary/[0.08] p-6 sm:p-8 space-y-4 shadow-xs relative overflow-hidden"
          >
            <div className="flex items-center justify-between">
              <Badge className="bg-primary text-primary-foreground font-extrabold text-xs px-3 py-1">
                {t("programs.tiffinSeva.registration.general.badge")}
              </Badge>
              <span className="text-xs font-black text-primary">
                {t("programs.tiffinSeva.registration.general.tag")}
              </span>
            </div>
            <div>
              <h3 className="font-serif text-2xl font-bold text-foreground">
                {t("programs.tiffinSeva.registration.general.title")}
              </h3>
              <p className="text-xs text-muted-foreground mt-1">
                {t("programs.tiffinSeva.registration.general.desc")}
              </p>
            </div>
            <ul className="space-y-2 text-xs text-foreground/80">
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-primary shrink-0" />
                <span>{t("programs.tiffinSeva.registration.general.bullet1")}</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-primary shrink-0" />
                <span>{t("programs.tiffinSeva.registration.general.bullet2")}</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-primary shrink-0" />
                <span>{t("programs.tiffinSeva.registration.general.bullet3")}</span>
              </li>
            </ul>
            <Button
              onClick={() => handleOpenModal("below_60")}
              className="btn-lift w-full bg-primary hover:bg-primary/95 text-primary-foreground font-bold text-xs gap-1.5"
            >
              <HeartHandshake className="h-4 w-4" /> {t("programs.tiffinSeva.registration.general.btn")}
            </Button>
          </Reveal>
        </div>

        {/* Schemes Section */}
        <div className="space-y-8 pt-10 border-t border-border/80">
          <Reveal className="text-center max-w-2xl mx-auto space-y-2">
            <Badge variant="secondary" className="bg-primary/10 text-primary font-bold text-xs px-3 py-1">
              Support Our Mission
            </Badge>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-foreground tracking-tight font-serif">
              Tiffin Seva Donation Schemes
            </h2>
            <p className="text-xs sm:text-sm text-muted-foreground">
              Choose a contribution plan below to support the daily preparation and delivery of pure Satvik meals.
            </p>
          </Reveal>

          {loadingSchemes ? (
            <div className="flex justify-center items-center py-12">
              <span className="text-sm text-muted-foreground animate-pulse">Loading schemes...</span>
            </div>
          ) : schemes.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-sm text-muted-foreground">No active schemes available at the moment.</p>
            </div>
          ) : (
            <>
              {/* Old Grid Design (Commented out) */}
              {/*
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto">
                {schemes.map((scheme, idx) => {
                  const icons = [UtensilsCrossed, Heart, Leaf, Clock, CalendarCheck, HeartHandshake];
                  const SchemeIcon = icons[idx % icons.length] || Heart;

                  return (
                    <div
                      key={scheme.id}
                      className="group flex gap-2 flex-col justify-between rounded-3xl border-2 border-border bg-card p-4 sm:p-5 shadow-xs transition-all duration-300 hover:-translate-y-1 hover:border-primary/50 hover:shadow-md"
                    >
                      <div className="space-y-3">
                        <div className="flex items-start justify-between gap-2">
                          <h3 className="!text-lg !font-sans text-foreground leading-snug group-hover:text-primary transition-colors">
                            {scheme.name}
                          </h3>
                          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary group-hover:scale-105 transition-transform">
                            <SchemeIcon className="h-4.5 w-4.5" />
                          </div>
                        </div>

                        <div className="flex items-baseline gap-1 pt-0.5">
                          <span className="text-2xl font-extrabold text-primary">
                            ₹{Number(scheme.price).toLocaleString("en-IN")}
                          </span>
                          <span className="text-[10px] text-muted-foreground font-medium"></span>
                        </div>
                      </div>

                      <div className="mt-2 pt-3 border-t border-border/80">
                        <Button
                          onClick={() => handleOpenDonation(scheme.id)}
                          className="w-full border-2 border-primary bg-primary-foreground hover:bg-primary text-primary hover:text-primary-foreground font-bold text-xs gap-1.5 py-2 rounded-xl shadow-xs"
                        >
                          <UtensilsCrossed className="h-4 w-4" /> Donate Now
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
              */}

              {/* Table Format Design */}
              <Reveal variant="wipe-up" className="max-w-6xl mx-auto overflow-hidden rounded-2xl border border-border/70 bg-card shadow-xs sm:rounded-3xl">
                {/* Table Header for Desktop */}
                <div className="hidden grid-cols-[44px_2.5fr_3fr_1.2fr_125px] items-center gap-4 border-b border-border/70 bg-background px-6 py-4 text-[12px] font-extrabold uppercase tracking-wider text-primary lg:grid">
                  <div className="text-center">#</div>
                  <div>SERVICE</div>
                  <div>DESCRIPTION / CATEGORY</div>
                  <div>AMOUNT</div>
                  <div className="text-right pr-1">ACTION</div>
                </div>

                {/* Table Body Rows */}
                <Stagger className="divide-y divide-border/40" step={80} delay={350}>
                  {schemes.map((scheme, idx) => {
                    const icons = [UtensilsCrossed, Heart, Leaf, Clock, CalendarCheck, HeartHandshake];
                    const SchemeIcon = icons[idx % icons.length] || Heart;

                    return (
                      <StaggerItem key={scheme.id} index={idx} from="left">
                      <div
                        className="group px-4 py-3 transition-colors duration-150 hover:bg-background sm:px-6 sm:py-4"
                      >
                        {/* Desktop Row */}
                        <div className="hidden grid-cols-[44px_2.5fr_3fr_1.2fr_125px] items-center gap-4 lg:grid">
                          {/* 1. # Index */}
                          <div className="text-center font-semibold text-muted-foreground">
                            {idx + 1}
                          </div>

                          {/* 2. Service Icon & Name */}
                          <div className="flex items-center gap-3 pr-2">
                            <div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                              <SchemeIcon className="h-4 w-4" />
                            </div>
                            <span className="text-[15px] font-bold text-foreground group-hover:text-primary transition-colors">
                              {scheme.name}
                            </span>
                          </div>

                          {/* 3. Description / Category */}
                          <div className="text-sm text-muted-foreground pr-2">
                            {scheme.description || scheme.category || scheme.name}
                          </div>

                          {/* 4. Amount */}
                          <div className="font-sans text-base font-extrabold text-primary">
                            ₹{Number(scheme.price).toLocaleString("en-IN")}
                          </div>

                          {/* 5. Action */}
                          <div className="flex justify-end pr-1">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="w-full max-w-[115px] rounded-full border-primary/40 bg-background px-3 py-1.5 text-xs font-bold text-primary shadow-xs transition-all hover:bg-primary hover:text-primary-foreground hover:shadow-sm"
                              onClick={() => handleOpenDonation(scheme.id)}
                            >
                              Sponsor Now
                            </Button>
                          </div>
                        </div>

                        {/* Mobile / Tablet Card View */}
                        <div className="flex flex-col gap-3 lg:hidden">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-center gap-2.5">
                              <span className="text-xs font-bold text-muted-foreground/80">
                                #{idx + 1}
                              </span>
                              <div className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
                                <SchemeIcon className="h-4 w-4" />
                              </div>
                              <h3 className="font-sans text-base font-bold text-foreground">
                                {scheme.name}
                              </h3>
                            </div>
                            <div className="text-base font-extrabold text-primary">
                              ₹{Number(scheme.price).toLocaleString("en-IN")}
                            </div>
                          </div>

                          {(scheme.description || scheme.category) && (
                            <p className="pl-7 text-xs text-muted-foreground">
                              {scheme.description || scheme.category}
                            </p>
                          )}

                          <div className="flex items-center justify-end gap-2 pt-2 border-t border-border/40">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="rounded-full border-primary/40 bg-background px-4 py-1.5 text-xs font-bold text-primary hover:bg-primary hover:text-primary-foreground"
                              onClick={() => handleOpenDonation(scheme.id)}
                            >
                              Sponsor Now
                            </Button>
                          </div>
                        </div>
                      </div>
                      </StaggerItem>
                    );
                  })}
                </Stagger>
              </Reveal>
            </>
          )}
        </div>

      </section>

      {/* Bank & UPI Details */}
      <ManavSevaBankDetails />

      {/* Tiffin Seva Registration Modal */}
      <TiffinSevaInquiryModal
        open={inquiryModalOpen}
        onOpenChange={setInquiryModalOpen}
        defaultCategory={selectedCategory}
      />

      {/* Tiffin Seva Donation Modal */}
      <TiffinSevaDonationModal
        open={donationModalOpen}
        onOpenChange={setDonationModalOpen}
        initialSchemeId={selectedSchemeId}
      />
    </PageShell>
  );
}
